import React, { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationFunctionOptions } from '@apollo/client';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import { Formik, FormikProps, FormikValues } from 'formik';
import { ExecutionResult } from 'graphql';
import useStore from 'store';
import { Organization } from 'types/schema-generated-types';
import { AlertVariant } from 'types/enums';
import * as Yup from 'yup';
import OrganizationDetailsForm from '../OrganizationDetailsForm';

interface EditOrganizationModalProps {
  updateOrganization: (
    options?: MutationFunctionOptions<any, Record<string, any>> | undefined
  ) => Promise<ExecutionResult<any>>;
  onClose: Function;
  organization: Organization;
}

const EditOrganizationModal = ({
  onClose,
  organization,
  updateOrganization
}: EditOrganizationModalProps): ReactElement => {
  const [, { openAlert }] = useStore();
  const { t } = useTranslation('EditOrganizationModal');
  const [form, setForm] = useState({});

  const closeModal = useCallback(
    (success?: boolean): void => {
      onClose(success);
    },
    [onClose]
  );

  const handleCancelClick = useCallback((): void => {
    closeModal();
    return;
  }, [closeModal]);

  const requiredFieldText = t('Field is required');

  const init = React.useMemo(
    () => ({
      name: organization.name
    }),
    [organization]
  );

  const handleFormikSubmit = useCallback(
    async values => {
      setForm({
        ...form,
        ...values
      });

      try {
        await updateOrganization({
          variables: {
            where: {
              id: organization.id
            },
            data: {
              name: values.name
            }
          }
        });

        openAlert({
          variant: AlertVariant.SUCCESS,
          title: t('Organization Updated'),
          text: t('Your organization details have been successfully updated.')
        });

        closeModal(true);
      } catch (err) {
        console.log(err);
      }
    },
    [form, setForm, closeModal, updateOrganization, organization.id, openAlert, t]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={init}
      validationSchema={Yup.object({
        name: Yup.string()
          .max(100)
          .required(requiredFieldText)
      })}
      validateOnChange={true}
      onSubmit={handleFormikSubmit}
    >
      {({ dirty, isSubmitting, isValid, submitForm }: FormikProps<FormikValues>): ReactElement => {
        return (
          <ModalContainer
            Header={<ModalHeader title={t('Edit Organization Details')} />}
            Footer={
              <ModalFooter
                onCancel={handleCancelClick}
                onSubmit={submitForm}
                submitDisabled={!dirty || !isValid || isSubmitting}
              />
            }
          >
            <ModalContent>
              <OrganizationDetailsForm />
            </ModalContent>
          </ModalContainer>
        );
      }}
    </Formik>
  );
};

export default EditOrganizationModal;
